import { dashboardAPI } from '../services/api';
import { handleRichPaste, SOCIAL_LINKS, SOCIAL_LOGO_ATTR, socialNetworkOf, imageLinkOf, linkImage } from '../components/RichEmailEditor';

/**
 * Make any pasted signature savable, whatever it arrives as.
 *
 * A signature copied out of Outlook, Gmail or a designer's HTML brings its
 * pictures with it as base64 inside the markup, and base64 is a third bigger
 * than the file it encodes. Two logos out of a modern signature (often a 1500px
 * PNG shown at 150px) is enough to push a template past **4.5 MB, which is the
 * hard limit on a request body in this hosting** — the save came back as a bare
 * `FUNCTION_PAYLOAD_TOO_LARGE`, which reads as "it just will not paste".
 *
 * Two steps, in this order:
 *
 *   1. Re-encode anything oversized at the size it is actually displayed at.
 *      A 1.4 MB logo drawn 150px wide becomes ~30 KB and looks identical. This
 *      alone settles almost every real signature.
 *   2. Anything still heavy is parked in our own storage and referenced by URL,
 *      so the body that gets saved is a few kilobytes of markup no matter what
 *      was pasted. `emailService` pulls those pictures back INTO the message
 *      when it sends, so the recipient still sees them inline rather than
 *      behind a "download pictures" bar.
 *
 * Everything here is best-effort: an image that cannot be shrunk or uploaded is
 * left exactly as it was. Nothing is ever refused, and nothing is thrown away.
 */

// Below this, an inline image costs less than the round trip to fix it. It used
// to be 120 KB, which let two ISO badges of 113 KB and 124 KB through untouched.
const SHRINK_OVER_BYTES = 30 * 1024;
// Retina headroom over the ~600px a mail client gives a signature. A CEILING —
// see the target calculation, which once read `Math.max(displayWidth * 2, this)`
// and therefore never shrank anything below 1400px wide.
const MAX_IMAGE_WIDTH = 1400;
// Comfortably under the 4.5 MB request cap, leaving room for the rest of a send
// (recipients, attachments, the quoted thread).
const SAFE_BODY_BYTES = 3 * 1024 * 1024;
// A picture heavier than this is parked in storage even when the body would
// still fit. A megabyte of base64 inside a template is re-parsed on every
// render of every row in the composer, so keeping bodies light is not only
// about whether the save goes through.
const HOST_OVER_BYTES = 400 * 1024;

const dataUrlBytes = (src) => {
  const comma = src.indexOf(',');
  if (comma < 0) return 0;
  return Math.floor((src.length - comma - 1) * 3 / 4);
};

const mimeOf = (src) => {
  const m = /^data:([^;,]+)[;,]/i.exec(src || '');
  return m ? m[1].toLowerCase() : '';
};

const isDataImage = (src) => /^data:image\//i.test(src || '');

/** Does the drawn image use its alpha channel? Decides PNG vs JPEG. */
const hasTransparency = (canvas, ctx) => {
  try {
    const { width, height } = canvas;
    const step = Math.max(1, Math.floor(Math.min(width, height) / 64));
    for (let y = 0; y < height; y += step) {
      const row = ctx.getImageData(0, y, width, 1).data;
      for (let x = 3; x < row.length; x += 4 * step) {
        if (row[x] < 250) return true;
      }
    }
  } catch (_) {
    return true;   // tainted canvas or no pixel access: assume it matters
  }
  return false;
};

// A picture that never decodes must never leave a paste hanging.
const DECODE_TIMEOUT_MS = 15000;

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  const timer = setTimeout(() => reject(new Error('image did not decode in time')), DECODE_TIMEOUT_MS);
  img.onload = () => { clearTimeout(timer); resolve(img); };
  img.onerror = () => { clearTimeout(timer); reject(new Error('image did not decode')); };
  img.src = src;
});

/** A 2D canvas, or null where there isn't one (jsdom, and old browsers). */
const canvasContext = () => {
  try {
    if (typeof document === 'undefined' || !document.createElement) return null;
    const canvas = document.createElement('canvas');
    if (!canvas || typeof canvas.getContext !== 'function') return null;
    const ctx = canvas.getContext('2d');
    return ctx ? { canvas, ctx } : null;
  } catch (_) {
    return null;
  }
};

/**
 * Re-encode one data: image at a sane size. Returns a new data URL, or null if
 * there is nothing worth doing (or the browser cannot do it).
 */
export const shrinkDataImage = async (src, displayWidth = 0) => {
  if (!isDataImage(src)) return null;
  const mime = mimeOf(src);
  // A GIF may be animated and a canvas would flatten it to one frame; an SVG is
  // vector and already tiny. Leave both alone.
  if (mime === 'image/gif' || mime === 'image/svg+xml') return null;
  if (dataUrlBytes(src) < SHRINK_OVER_BYTES) return null;
  // Before decoding anything: without a canvas there is nothing to re-encode
  // with, and waiting on an image we cannot use would stall the paste.
  const surface = canvasContext();
  if (!surface) return null;
  const { canvas, ctx } = surface;

  try {
    const img = await loadImage(src);
    const natural = img.naturalWidth || img.width;
    if (!natural) return null;
    // Never enlarge, never go below what it is drawn at on screen — and never
    // keep more than twice the pixels it is displayed with.
    //
    // This line read `Math.max(displayWidth * 2, MAX_IMAGE_WIDTH)`, which made
    // 1400px a FLOOR rather than a ceiling: an ISO badge of 1400x700 shown at
    // 67x28 was judged "already small enough" and travelled at 124 KB in every
    // email. Five of one BDA's templates were 4 MB each because of it.
    const wanted = displayWidth > 0
      ? Math.min(Math.max(displayWidth * 2, 120), MAX_IMAGE_WIDTH)
      : MAX_IMAGE_WIDTH;
    const target = Math.min(natural, wanted);
    if (target >= natural * 0.95 && dataUrlBytes(src) < HOST_OVER_BYTES) return null;
    const scale = target / natural;
    canvas.width = Math.max(1, Math.round(natural * scale));
    canvas.height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const keepAlpha = mime !== 'image/jpeg' && hasTransparency(canvas, ctx);
    const candidates = keepAlpha
      ? [canvas.toDataURL('image/png')]
      : [canvas.toDataURL('image/jpeg', 0.85), canvas.toDataURL('image/png')];
    const best = candidates
      .filter(Boolean)
      .sort((a, b) => a.length - b.length)[0];
    // Only keep it if it actually helped.
    return best && best.length < src.length * 0.9 ? best : null;
  } catch (_) {
    return null;
  }
};

/* --------------------------------------------------------------------------
 * Recognising the LinkedIn and Instagram badges by LOOK.
 *
 * The badges are pictures, so they are dead unless something wraps them in a
 * link. `SOCIAL_LOGO_SIGNATURES` does that for encodings we have seen before,
 * but a signature re-pasted out of Outlook, re-encoded on the way in or simply
 * saved at a different size produces different bytes every time — and then the
 * badge silently stops being clickable. That is the bug BDEs kept reporting
 * and could not fix themselves, and it is unfixable by adding more byte
 * patterns, because there is always one more.
 *
 * The picture does not change, though. Drawn down to 8x8 on white, the
 * LinkedIn glyph and the Instagram glyph each look the same whatever file they
 * arrived in. So: decode, shrink to 8x8, compare with the reference, and link
 * anything close enough.
 *
 * Measured on every image in all 70 stored templates (66 distinct pictures, 16
 * of them badges): the worst real badge scores 17, and the closest thing that
 * is NOT a badge scores 36. The cutoff is 25, with a squareness check on top,
 * so there is roughly a 50% margin on both sides. Pointing somebody's product
 * photo at our LinkedIn page would be worse than a dead badge, which is why
 * the bar is set where nothing else comes near it.
 * ----------------------------------------------------------------------- */

// 8x8 RGB, composited on white, as hex — taken from real badges in the
// templates. Two references per network: the flat modern glyph and the older
// one with a white margin around it.
const SOCIAL_LOGO_LOOK = {
  linkedin: [
    '95bde590bae48eb9e48db9e48db9e48db9e4b9d3eef9fbfd126fc85597d71470c90065c50065c50065c54d92d5eef4fb1671c969a0db2076cb0968c51571c90769c64c92d5eef4fb1f76cb99c0e68cb8e4bcd6efcadef27eb0e04f93d5eef4fb2379ccb0ceeca4c7e99dc3e7458bd2a9c8ea5a98d8eef4fb2379ccb0ceeca4c7e988b7e43584d0a9c9ea5b99d8eef4fb106dc74c91d5478ed33b87d11672c9498fd45395d6cbe0f2a2c5e89bc2e79bc2e79ac2e799c2e79bc2e7c0d8f0dfebf7',
    'fffffffefefef3f8fcf2f7fcf2f7fcf2f7fcfcfdfefffffffefefe86b9e80c70cf0c6fce0c6fce0c70ce4f98ddfbfdfef7fafd0c72d0b0d0ee2c82d20c6fcc0c6fcc0c6fcce2eefaf6fafd0c71d08fbce76fa9e07fb3e482b4e40c6fcce0edfaf6fafd0c72d0d2e4f5b7d4ef4890d7eaf2fa1372cde0edfaf6fafd0c71d0c7def3adceed257cd0dae8f71372cde1eefafefefe67a6e21172ce1071cd0c6fcc1172cd378ad9f8fafdfffffffdfdfee7f1fae5f0fae5f0fae5f0faf9fbfdffffff'
  ],
  instagram: [
    'dbbfe79c5aca9858cb9a58cc9a58cb9854c99d56c7dcbfe7b258b7e5ccebfbf5faebc9e4eacae4e8c8e5cd93d2af54b4c352a2fbf7f9de9bc5d27fbbd17ebbce73b3e9c6e2c04ea0ce5794f0ceddda85b0f6e4eef6e3eeda82aff0ccdccf5793d26584f1cfdcde8da9f8e7eaf8e7eadf8fa7f2d1dcd16586d47579fdf8f9e6aeb5df9a9cde9a9ce5adb6fcf7f8d5747ada8e74f3dfd3fcf9f8f3d8d6f3d8d7fcf8f8f3ded4db8c77f3d9c9e2a570e3a66be3a96ce4a96ce3a66ce3a471f3dac9',
    'fffffffefefee3ccefcfbeeac6c3ebcbcff1fbfbfdffffffffffffedd6efdfb6e5f1ebf8efedf9e0ddf4b49ee1ffffffffffffdc8acfe5a6d8e79dc9e39ac7fefdfeb37ecaffffffffffffe581c4fdf5f8f4cbdffaebf3f2c7d9cc7ebaffffffffffffea82bcfcf4f7f9d2dcfdf1eef9cccae7909bffffffffffffeb87b8fef9fbf99d97fcae8afefefdf8a680fffffffffffff7cee1f8d2defffffffffffffef7edfdc991fffffffffffffefdfef9c1c4fdd7b4feebc1feefc9fefcfaffffff'
  ]
};

const LOOK_GRID = 8;
// A badge scores at most 17 against its reference; nothing else in any real
// template scores under 36.
const LOOK_MAX_DISTANCE = 25;
// Both glyphs are square or nearly so. A wordmark, a photo or a certification
// banner is not, and this is what keeps the ones that scored 36 well clear.
const LOOK_MAX_ASPECT = 2;

const hexToBytes = (hex) => {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
  return out;
};
const SOCIAL_LOGO_LOOK_BYTES = Object.fromEntries(
  Object.entries(SOCIAL_LOGO_LOOK).map(([net, refs]) => [net, refs.map(hexToBytes)])
);

/** Mean per-channel difference between two fingerprints, 0 (identical) to 255. */
const lookDistance = (a, b) => {
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += Math.abs(a[i] - b[i]);
  return sum / a.length;
};

// The same picture appears in every template a BDE owns, and a send walks many
// rows — decode each distinct image once per page load.
const lookCache = new Map();

/**
 * Draw one image small and read its pixels. Null when it cannot be decoded,
 * cannot be read (a cross-origin picture taints the canvas) or is not the
 * shape of a badge.
 */
const fingerprintImage = async (src) => {
  if (lookCache.has(src)) return lookCache.get(src);
  let fp = null;
  try {
    const img = await loadImage(src);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (w && h && Math.max(w / h, h / w) <= LOOK_MAX_ASPECT) {
      const surface = canvasContext();
      if (surface) {
        const { canvas, ctx } = surface;
        canvas.width = LOOK_GRID;
        canvas.height = LOOK_GRID;
        // Both glyphs are drawn on transparency in some copies and on white in
        // others; compositing on white makes the two the same picture.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, LOOK_GRID, LOOK_GRID);
        ctx.drawImage(img, 0, 0, LOOK_GRID, LOOK_GRID);
        const px = ctx.getImageData(0, 0, LOOK_GRID, LOOK_GRID).data;
        const out = new Uint8Array(LOOK_GRID * LOOK_GRID * 3);
        for (let i = 0, j = 0; i < px.length; i += 4) { out[j++] = px[i]; out[j++] = px[i + 1]; out[j++] = px[i + 2]; }
        fp = out;
      }
    }
  } catch (_) {
    fp = null;
  }
  lookCache.set(src, fp);
  return fp;
};

/** Which network this picture looks like, or null. */
const networkByLook = async (src) => {
  const fp = await fingerprintImage(src);
  if (!fp) return null;
  let best = null;
  for (const [net, refs] of Object.entries(SOCIAL_LOGO_LOOK_BYTES)) {
    for (const ref of refs) {
      const d = lookDistance(fp, ref);
      if (!best || d < best.d) best = { net, d };
    }
  }
  return best && best.d <= LOOK_MAX_DISTANCE ? best.net : null;
};

/**
 * Point every AGS social badge in a body at the right page.
 *
 * A link somebody set by hand always wins — this only ever fills in a badge
 * that would otherwise be dead. Best-effort throughout: a picture that will
 * not decode is simply left alone.
 *
 * Returns the number of badges it linked.
 */
export const linkSocialLogosInDom = async (root) => {
  if (!root) return 0;
  const images = [...root.querySelectorAll('img')].filter(img => !imageLinkOf(img));
  if (images.length === 0) return 0;
  let linked = 0;
  for (const img of images) {
    // The byte-for-byte list is free, so try it before decoding anything.
    let network = socialNetworkOf(img);
    if (!network) {
      const src = img.getAttribute('src') || '';
      if (!src) continue;
      network = await networkByLook(src);
      // Remember the answer on the tag so nothing has to decode it again —
      // including renderEmailBody, which is synchronous and cannot.
      if (network) img.setAttribute(SOCIAL_LOGO_ATTR, network);
    }
    const url = network && SOCIAL_LINKS[network];
    if (!url) continue;
    linkImage(img, url);
    linked += 1;
  }
  return linked;
};

/** The same thing, on a string of HTML. Returns the HTML, changed or not. */
export const linkSocialLogos = async (html) => {
  if (!html || typeof html !== 'string' || !/<img/i.test(html)) return html || '';
  if (typeof DOMParser === 'undefined') return html;
  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  } catch (_) {
    return html;
  }
  const root = doc.getElementById('root');
  if (!root) return html;
  const linked = await linkSocialLogosInDom(root);
  return linked ? root.innerHTML : html;
};

/** Park one image in storage and hand back its URL, or null if that fails. */
const hostImage = async (dataUrl) => {
  try {
    const res = await dashboardAPI.uploadEmailImage(dataUrl);
    return res && res.url ? res.url : null;
  } catch (_) {
    return null;
  }
};

/**
 * Bring a body down to something that will always save.
 *
 * Shrinks oversized pictures first, then hosts whatever is still keeping the
 * body over the limit — biggest first, stopping as soon as it fits. Returns the
 * HTML (unchanged if nothing needed doing) — never throws.
 */
export const slimEmailBody = async (html, { budget = SAFE_BODY_BYTES } = {}) => {
  if (!html || typeof html !== 'string' || !/<img/i.test(html)) return html || '';
  if (typeof DOMParser === 'undefined') return html;

  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  } catch (_) {
    return html;
  }
  const root = doc.getElementById('root');
  if (!root) return html;

  // Before anything is re-encoded or parked in storage: point the LinkedIn and
  // Instagram badges at the right pages. This is the one place every body
  // passes through on its way to being saved AND on its way to being sent, so
  // doing it here covers every template and every BDE without anybody having
  // to remember. The marker it leaves on the tag survives the re-encoding
  // below, so a shrunk badge is still recognised next time.
  let changed = (await linkSocialLogosInDom(root)) > 0;

  const images = [...root.querySelectorAll('img')].filter(img => isDataImage(img.getAttribute('src')));
  if (images.length === 0) return changed ? root.innerHTML : html;

  // 1. Shrink. Cheap, local, and usually enough on its own.
  for (const img of images) {
    const src = img.getAttribute('src');
    const shown = parseInt(img.getAttribute('width'), 10) || parseInt(img.style.width, 10) || 0;
    const smaller = await shrinkDataImage(src, shown);
    if (smaller) { img.setAttribute('src', smaller); changed = true; }
  }

  // 2. Park the heavy ones in storage — every picture still over the per-image
  // limit, and then, if the body is somehow STILL too big, the largest of what
  // is left until it fits.
  const sizeNow = () => root.innerHTML.length;
  const inlineImages = () => [...root.querySelectorAll('img')]
    .filter(img => isDataImage(img.getAttribute('src')))
    .sort((a, b) => (b.getAttribute('src') || '').length - (a.getAttribute('src') || '').length);

  for (const img of inlineImages()) {
    if (dataUrlBytes(img.getAttribute('src')) < HOST_OVER_BYTES) break;   // sorted: the rest are smaller
    const url = await hostImage(img.getAttribute('src'));
    if (url) { img.setAttribute('src', url); changed = true; }
  }
  for (const img of inlineImages()) {
    if (sizeNow() <= budget) break;
    const url = await hostImage(img.getAttribute('src'));
    if (url) { img.setAttribute('src', url); changed = true; }
  }

  return changed ? root.innerHTML : html;
};

/**
 * The same treatment, applied to what is sitting in an editor right now.
 *
 * Called after a paste: the content goes in immediately (so the paste always
 * feels instant and nothing is ever blocked), and the pictures are settled a
 * moment later. `onChange` receives the tidied HTML so it is what gets saved.
 */
export const slimEditorImages = async (editorEl, onChange) => {
  if (!editorEl) return;
  const before = editorEl.innerHTML;
  // A pasted signature usually brings its pictures as data: URLs, but not
  // always — and the badges still have to be linked either way.
  if (!/<img/i.test(before)) return;
  const after = await slimEmailBody(before);
  if (after === before) return;
  // Only write back if the person has not typed something else in the meantime.
  if (editorEl.innerHTML !== before) return;
  editorEl.innerHTML = after;
  onChange?.(after);
};

export const EMAIL_IMAGE_LIMITS = { SHRINK_OVER_BYTES, MAX_IMAGE_WIDTH, SAFE_BODY_BYTES, HOST_OVER_BYTES };

/**
 * The paste handler the editors actually use: paste first, tidy the pictures a
 * beat later.
 *
 * Wrapping it this way keeps the paste itself instant and unconditional — the
 * content is in the editor before any of the image work starts, and if that
 * work fails the paste still stands exactly as it arrived.
 */
export const handleRichPasteWithImages = (e, editorEl, onDone) => {
  const settle = (html) => {
    onDone?.(html);
    slimEditorImages(editorEl, onDone).catch(() => {});
  };
  return handleRichPaste(e, editorEl, settle);
};
